/**
 * Bibliographic audit, 2026-10-29. URLs identify the publisher's publication.
 * A verified publication is NOT verification of an individual engine threshold.
 * NICE codes identify living guidelines: original publication year must not be
 * presented as the revision used by the workbook, which does not record it.
 */
export interface ClinicalReferencePublication {
  name: string;
  title: string;
  url: string;
  year?: number;
  yearKind?: 'edition' | 'publication';
  replacedBy?: string;
}

const nice = (code: string, title: string, year: number, replacedBy?: string): ClinicalReferencePublication => ({
  name: `NICE ${code}`, title, year, yearKind: 'publication',
  url: `https://www.nice.org.uk/guidance/${code.toLowerCase()}`,
  ...(replacedBy ? { replacedBy } : {}),
});

export const clinicalReferencePublications: Record<string, ClinicalReferencePublication> = {
  'NICE NG17': nice('NG17', 'Type 1 diabetes in adults: diagnosis and management', 2015),
  'NICE NG28': nice('NG28', 'Type 2 diabetes in adults: management', 2015),
  'NICE NG3': nice('NG3', 'Diabetes in pregnancy: management from preconception to the postnatal period', 2015),
  'NICE NG145': nice('NG145', 'Thyroid disease: assessment and management', 2019),
  'NICE NG201': nice('NG201', 'Antenatal care', 2021),
  'NICE NG203': nice('NG203', 'Chronic kidney disease: assessment and management', 2021),
  'NICE CG181': nice('CG181', 'Cardiovascular disease: risk assessment and reduction, including lipid modification', 2014, 'NICE NG238'),
  'NICE NG136': nice('NG136', 'Hypertension in adults: diagnosis and management', 2019),
  'NICE CG61': nice('CG61', 'Irritable bowel syndrome in adults: diagnosis and management', 2008),
  'NICE NG129': nice('NG129', 'Crohn’s disease: management', 2019),
  'NICE NG20': nice('NG20', 'Coeliac disease: recognition, assessment and management', 2015),
  'NICE NG69': nice('NG69', 'Eating disorders: recognition and treatment', 2017),
  'NICE NG222': nice('NG222', 'Depression in adults: treatment and management', 2022),
  'NICE CG189': nice('CG189', 'Obesity: identification, assessment and management', 2014, 'NICE NG246'),
  'NICE NG8': nice('NG8', 'Chronic kidney disease: managing anaemia', 2015, 'NICE NG203'),
  'ADA SoC 2024': {
    name: 'ADA', title: 'Standards of Care in Diabetes', year: 2024, yearKind: 'edition',
    url: 'https://diabetesjournals.org/care/issue/47/Supplement_1',
  },
  'KDIGO 2024': {
    name: 'KDIGO', title: 'Clinical Practice Guideline for the Evaluation and Management of Chronic Kidney Disease',
    year: 2024, yearKind: 'edition', url: 'https://kdigo.org/guidelines/ckd-evaluation-and-management/',
  },
  'EASL/EASD/EASO 2024': {
    name: 'EASL–EASD–EASO', title: 'Clinical Practice Guidelines on the management of metabolic dysfunction-associated steatotic liver disease (MASLD)',
    year: 2024, yearKind: 'edition',
    url: 'https://easl.eu/publication/easl-easd-easo-clinical-practice-guidelines-managment-of-metabolic-dysfunction-associated-steatotic-liver-disease/',
  },
  'ESHRE/ASRM 2023': {
    name: 'International PCOS guideline (ESHRE/ASRM)', title: 'Assessment and management of polycystic ovary syndrome',
    year: 2023, yearKind: 'edition', url: 'https://www.eshre.eu/Guidelines-and-Legal/Guidelines/Polycystic-Ovary-Syndrome',
  },
  'SACN Lower Carb 2021': {
    name: 'SACN / Diabetes UK', title: 'Lower carbohydrate diets for adults with type 2 diabetes', year: 2021, yearKind: 'edition',
    url: 'https://www.gov.uk/government/publications/sacn-report-lower-carbohydrate-diets-for-type-2-diabetes',
  },
  'SACN Vit D 2016': {
    name: 'SACN', title: 'Vitamin D and health', year: 2016, yearKind: 'edition',
    url: 'https://www.gov.uk/government/publications/sacn-vitamin-d-and-health-report',
  },
  'NHS pregnancy foods to avoid': {
    name: 'NHS', title: 'Foods to avoid in pregnancy',
    // The live page's review date does not establish the version used by a saved plan.
    url: 'https://www.nhs.uk/pregnancy/keeping-well/foods-to-avoid/',
  },
};

/** These workbook abbreviations do not identify an unambiguous publication/edition. */
export const unverifiedClinicalReferences: Record<string, string> = {
  'Ali et al. BMJ 2016': 'The author, journal and year could not be matched to an identifiable publication.',
  'BDA': 'No document title or edition is recorded.',
  'BDA Cholesterol': 'No document title or edition is recorded.',
  'BDA GLP-1': 'No document title or edition is recorded.',
  'BDA IBS': 'No document title or edition is recorded.',
  'BDA Iron': 'No document title or edition is recorded.',
  'BDA Mental Health': 'No document title or edition is recorded.',
  'BDA Mood': 'No document title or edition is recorded.',
  'BDA PCOS': 'The workbook does not identify which BDA PCOS document or edition was used.',
  'BDA Plant-based': 'No document title or edition is recorded.',
  'BDA Renal': 'No document title or edition is recorded.',
  'BDA Thyroid': 'No document title or edition is recorded.',
  'Coeliac UK': 'An organisation is recorded, not a document or edition.',
  'DASH': 'An eating pattern is recorded, not a specific study or guideline edition.',
  'EASL 2024': 'EASL published multiple guidelines in 2024; the cited title is not recorded.',
  'ESC': 'No document title or edition is recorded.',
  'ESC/EAS': 'The cited edition is not recorded; the 2019 guideline and 2025 update are distinct.',
  'ESPEN IBD': 'The cited edition is not recorded; the 2017, 2020 and 2023 publications are distinct.',
  'ESPEN Liver': 'The cited edition is not recorded; the 2019 guideline and 2020 practical guideline are distinct.',
  'Food Safety Act + BDA': 'No jurisdiction, provision or BDA document is recorded.',
  'GLP-1 guidance': 'No publisher, document title or edition is recorded.',
  'Monash': 'No document title or edition is recorded.',
  'NHS': 'No page title or version is recorded.',
  'NICE B12': 'No guideline code or edition is recorded; do not assume NG239.',
  'NICE iron-def': 'No guideline code or edition is recorded.',
  'SACN Children': 'No report title or edition is recorded.',
  'SACN Maternal': 'No report title or edition is recorded.',
};

// Clinical applicability issues are retained for review rather than silently
// attributing existing numeric rules to a different/newer guideline.
export const clinicalReferenceApplicabilityNotes: Record<string, Record<string, string>> = {
  'NICE NG8': { haemoglobin: 'CKD-specific guidance; applicability to this general haemoglobin insight needs review.' },
  'NICE NG28': { prediabetes: 'Type 2 diabetes guidance; applicability to this prediabetes insight needs review.' },
  'NICE NG129': { 'ibd crohn s uc': 'Crohn’s guidance; this citation does not identify ulcerative-colitis guidance.' },
};
